<template>
  <ModalView
    v-model="visible"
    title="Missing Evidence"
    :hide-ok="isBulk"
    :ok-title="okLabel"
    ok-variant="warning"
    :hide-cancel="retrying"
    @ok="proceed"
    @cancel="dismiss"
  >
    <!-- Single letter mode -->
    <template v-if="!isBulk">
      <p v-if="isPending">
        The screenshot for this letter is still being captured. You can wait for it to finish, or proceed without evidence.
      </p>
      <p v-else>
        This letter doesn't have an evidence screenshot attached.
      </p>
    </template>

    <!-- Bulk mode -->
    <template v-else>
      <template v-if="retrying">
        <p>
          {{ retrySummary }}
        </p>
        <div class="d-flex align-items-center gap-2">
          <div class="progress flex-grow-1" style="height: 6px;">
            <div
              class="progress-bar"
              :style="{ width: progressPercent + '%' }"
            />
          </div>
          <span class="small text-muted">{{ capturedCount }} / {{ retriedCount }}</span>
        </div>
        <div v-if="retryTimedOut" class="mt-3 small text-warning">
          Some screenshots are still processing.
        </div>
        <div class="d-flex flex-column gap-2 mt-3">
          <button type="button" class="btn btn-warning" @click="resolveAndClose('all')">
            Print all {{ totalCount }} letters now
          </button>
          <button v-if="capturedCount > 0" type="button" class="btn btn-outline-secondary" @click="resolveAndClose('exclude')">
            Print only {{ totalCount - stillMissingCount }} with evidence
          </button>
        </div>
      </template>
      <template v-else>
        <p>
          {{ stillMissingCount }} of {{ totalCount }} {{ totalCount === 1 ? 'letter is' : 'letters are' }} missing evidence screenshots.
        </p>
        <div class="d-flex flex-column gap-2">
          <button type="button" class="btn btn-warning" @click="resolveAndClose('all')">
            Print all {{ totalCount }} letters
          </button>
          <button type="button" class="btn btn-outline-secondary" @click="resolveAndClose('exclude')">
            Print only {{ totalCount - stillMissingCount }} with evidence
          </button>
          <button type="button" class="btn btn-outline-primary" @click="retryAll">
            <i class="bi-arrow-clockwise" /> Retry all failed
          </button>
        </div>
      </template>
    </template>
  </ModalView>
</template>

<script>
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from '@/db';
import { postApi } from '@/api';
import ModalView from '@/components/common/ModalView.vue';

function needsGateStatus(status) {
  return status === 'pending' || status === 'failed';
}

function needsGate(evidence) {
  return needsGateStatus(evidence?.status);
}

function isEvidenceComplete(status) {
  return status === 'captured' || status === 'manual_upload';
}

function shouldTrackBatchStatus(status) {
  return status === 'enqueued' || status === 'already_pending';
}

const RETRY_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Gating dialog shown before send/print when evidence is pending or failed.
 *
 * Single letter:
 *   this.$refs.gate.guard(evidence) → Promise<boolean>
 *
 * Bulk print:
 *   this.$refs.gate.guardBulk(items) → Promise<'all' | 'exclude' | false>
 *   Each item must have item.context.evidence, item.context.listingId, item.context.cityId
 */
export default {
  components: { ModalView },
  data() {
    return {
      visible: false,
      status: null,
      isBulk: false,
      missingCount: 0,
      totalCount: 0,
      pendingResolve: null,
      // Retry-all state
      retrying: false,
      retryTimedOut: false,
      retriedCount: 0,
      enqueuedCount: 0,
      capturedCount: 0,
      evidenceStatusByListingId: {},
      gateItems: null,
      trackedListingIds: new Set(),
      snapshotUnsubs: [],
      retryTimeout: null
    };
  },
  computed: {
    isPending() {
      return this.status === 'pending';
    },
    okLabel() {
      return this.isPending ? 'Proceed without waiting' : 'Proceed without evidence';
    },
    retrySummary() {
      if (this.enqueuedCount === this.retriedCount) {
        return `Retrying ${this.retriedCount} screenshots...`;
      }
      if (this.enqueuedCount === 0) {
        return `Waiting on ${this.retriedCount} screenshots already in progress...`;
      }
      return `Retrying ${this.enqueuedCount} screenshots and waiting on ${this.retriedCount - this.enqueuedCount} already in progress...`;
    },
    progressPercent() {
      return this.retriedCount > 0 ? Math.round((this.capturedCount / this.retriedCount) * 100) : 0;
    },
    stillMissingCount() {
      if (!this.gateItems) return this.missingCount;
      return this.gateItems.filter(item => needsGateStatus(this.getItemEvidenceStatus(item))).length;
    }
  },
  methods: {
    /**
     * Single letter gate.
     */
    guard(evidence) {
      if (!needsGate(evidence)) {
        return Promise.resolve(true);
      }
      this.isBulk = false;
      this.status = evidence?.status;
      this.visible = true;
      return new Promise(r => { this.pendingResolve = r; });
    },

    /**
     * Bulk print gate.
     * Each item should have { letter, context } where context has evidence, listingId, cityId.
     */
    guardBulk(items) {
      const missing = items.filter(item => needsGate(item.context?.evidence));
      if (missing.length === 0) {
        return Promise.resolve('all');
      }
      this.isBulk = true;
      this.missingCount = missing.length;
      this.totalCount = items.length;
      this.gateItems = items;
      this.resetTrackingState();
      this.evidenceStatusByListingId = Object.fromEntries(
        items
          .map(item => [item.context?.listingId, item.context?.evidence?.status || null])
          .filter(([id]) => id)
      );
      this.visible = true;
      return new Promise(r => { this.pendingResolve = r; });
    },

    async retryAll() {
      const items = this.gateItems;
      if (!items?.length) return;

      const missing = items.filter(item => needsGateStatus(this.getItemEvidenceStatus(item)));
      if (missing.length === 0) return;

      this.retrying = true;
      this.retryTimedOut = false;
      this.capturedCount = 0;

      // Derive cityId from the first item
      const cityId = missing[0].context?.cityId;
      const listingIds = [...new Set(missing.map(item => item.context?.listingId).filter(Boolean))];

      let result;
      try {
        result = await postApi(`/v1/cities/${cityId}/evidence/batch-retry`, { listingIds });
      } catch (err) {
        console.error('Batch retry failed:', err);
        this.retrying = false;
        return;
      }

      const trackedIds = (result?.items || [])
        .filter(item => shouldTrackBatchStatus(item.status))
        .map(item => item.listingId);
      this.enqueuedCount = (result?.items || []).filter(item => item.status === 'enqueued').length;
      this.retriedCount = trackedIds.length;
      this.trackedListingIds = new Set(trackedIds);
      this.capturedCount = this.countCaptured();

      if (this.retriedCount === 0) {
        this.retrying = false;
        return;
      }

      // Listen for evidence updates on each listing document
      this.startBatchListening(cityId, trackedIds);

      // Timeout after 2 minutes
      this.retryTimeout = setTimeout(() => {
        this.retryTimedOut = true;
      }, RETRY_TIMEOUT_MS);
    },

    async startBatchListening(cityId, listingIds) {
      this.stopBatchListening();

      const firestore = await db;

      for (const listingId of listingIds) {
        const docRef = doc(firestore, `cities/${cityId}/listings/${listingId}`);
        const unsub = onSnapshot(docRef, (snapshot) => {
          if (!snapshot.exists()) return;
          const data = snapshot.data();
          const status = data?.evidence?.status;

          if (this.evidenceStatusByListingId[listingId] !== status) {
            this.evidenceStatusByListingId = { ...this.evidenceStatusByListingId, [listingId]: status };
            this.capturedCount = this.countCaptured();

            if (this.capturedCount >= this.retriedCount || !this.hasPendingTrackedItems()) {
              this.retrying = false;
              this.stopBatchListening();
            }
          }
        });

        this.snapshotUnsubs.push(unsub);
      }
    },

    countCaptured() {
      let count = 0;
      for (const id of this.trackedListingIds) {
        if (isEvidenceComplete(this.evidenceStatusByListingId[id])) {
          count++;
        }
      }
      return count;
    },
    getItemEvidenceStatus(item) {
      const listingId = item?.context?.listingId;
      return (listingId && this.evidenceStatusByListingId[listingId]) || item?.context?.evidence?.status || null;
    },
    hasPendingTrackedItems() {
      for (const id of this.trackedListingIds) {
        if (needsGateStatus(this.evidenceStatusByListingId[id])) {
          return true;
        }
      }
      return false;
    },
    resetTrackingState() {
      this.stopBatchListening();
      this.retrying = false;
      this.retryTimedOut = false;
      this.retriedCount = 0;
      this.enqueuedCount = 0;
      this.capturedCount = 0;
      this.trackedListingIds = new Set();
    },

    stopBatchListening() {
      for (const unsub of this.snapshotUnsubs) {
        unsub();
      }
      this.snapshotUnsubs = [];
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
        this.retryTimeout = null;
      }
    },

    resolveAndClose(value) {
      this.resetTrackingState();
      const r = this.pendingResolve;
      this.pendingResolve = null;
      this.gateItems = null;
      this.evidenceStatusByListingId = {};
      this.visible = false;
      r?.(value);
    },
    proceed() {
      this.resolveAndClose(true);
    },
    dismiss() {
      this.resolveAndClose(false);
    }
  },
  watch: {
    visible(v) {
      if (!v && this.pendingResolve) {
        this.resetTrackingState();
        this.pendingResolve(false);
        this.pendingResolve = null;
        this.gateItems = null;
        this.evidenceStatusByListingId = {};
      }
    }
  },
  beforeUnmount() {
    this.stopBatchListening();
  }
};
</script>
